import React, { useState, useRef, useEffect } from 'react';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { collection, onSnapshot } from "firebase/firestore";
import { db, resolveRecipientsCallable } from '../firebase/config';
import { PaperclipIcon, CameraIcon, VideoIcon, MicIcon, ClockIcon } from '../components/Icons';
import MediaRecorderModal from './MediaRecorderModal';
import { RadioGroup, Switch, Popover } from '@headlessui/react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { format } from 'date-fns';

const disappearOptions = [
  { name: 'Keep forever', value: 'none' },
  { name: 'Disappear after viewing', value: 'on_view' },
  { name: 'Disappear after a set time', value: 'after_time' },
];

const CreateCapsuleModal = ({ onSeal, onClose, isVisible, user, initialRecipient = '', initialTitle = '' }) => {
  const [title, setTitle] = useState(initialTitle);
  const [message, setMessage] = useState('');
  const [recipientEmail, setRecipientEmail] = useState(initialRecipient);
  const [selectedDate, setSelectedDate] = useState(null);
  const [unlockDate, setUnlockDate] = useState('');
  const [isImmediate, setIsImmediate] = useState(false);
  const [isGroupSend, setIsGroupSend] = useState(false);
  const [mediaFiles, setMediaFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [recordingType, setRecordingType] = useState(null);
  const [recipientError, setRecipientError] = useState('');
  const storage = getStorage();
  const [disappearOption, setDisappearOption] = useState('none'); // 'none', 'on_view', 'after_time'
  const [disappearHours, setDisappearHours] = useState(24);
  const [friends, setFriends] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Fetch friends list for suggestions
  useEffect(() => {
    if (isVisible && user) {
      const friendsRef = collection(db, 'users', user.uid, 'friends');
      const unsubscribe = onSnapshot(friendsRef, (snapshot) => {
        const friendsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setFriends(friendsList);
      });
      return () => unsubscribe(); // Cleanup listener on modal close
    }
  }, [isVisible, user]);

  const handleRecipientChange = (e) => {
    const value = e.target.value;
    setRecipientEmail(value);

    const lastEntry = value.split(',').pop().trim();
    if (lastEntry.startsWith('@') && lastEntry.length > 1) {
      const searchTerm = lastEntry.substring(1).toLowerCase();
      const filteredFriends = friends.filter(friend =>
        friend.username.toLowerCase().startsWith(searchTerm)
      );
      setSuggestions(filteredFriends);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (username) => {
    const parts = recipientEmail.split(',');
    parts.pop(); // Remove the partial entry
    parts.push(`@${username}`);
    setRecipientEmail(parts.join(', ') + ' ');
    setShowSuggestions(false);
  };

  const handleDateSelect = (date) => {
    const time = selectedDate ? format(selectedDate, 'HH:mm') : '00:00';
    const [hours, minutes] = time.split(':');
    date.setHours(parseInt(hours, 10));
    date.setMinutes(parseInt(minutes, 10));
    setSelectedDate(date);
    setUnlockDate(date.toISOString());
  };

  const handleTimeChange = (e) => {
    const time = e.target.value;
    const date = selectedDate || new Date();
    const [hours, minutes] = time.split(':');
    date.setHours(parseInt(hours, 10));
    date.setMinutes(parseInt(minutes, 10));
    setSelectedDate(new Date(date));
    setUnlockDate(date.toISOString());
  };

  const handleMediaCaptured = (file) => {
    setMediaFiles(prevFiles => [...prevFiles, file]);
    setRecordingType(null);
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    setMediaFiles(prevFiles => [...prevFiles, ...files]);
  };

  const removeMedia = (indexToRemove) => {
    setMediaFiles(prevFiles => prevFiles.filter((_, index) => index !== indexToRemove));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowSuggestions(false);
    setRecipientError('');
    if (!title || !recipientEmail || (!unlockDate && !isImmediate) || (!message && mediaFiles.length === 0)) {
      console.warn("Please fill out required fields."); return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    const identifiers = recipientEmail.split(',').map(id => id.trim().replace(/^@/, '')).filter(Boolean);
    try {
      const result = await resolveRecipientsCallable({ recipients: identifiers });
      const { resolved, unresolved } = result.data;

      if (unresolved.length > 0) {
        setRecipientError(`Could not find user(s): ${unresolved.join(', ')}`);
        setIsUploading(false);
        return;
      }

      let finalCapsuleData = {
        title,
        message,
        resolvedEmails: resolved,
        unlockDate: new Date(unlockDate),
        isImmediate,
        disappearOption,
        disappearHours: disappearOption === 'after_time' ? disappearHours : null,
      };

      if (mediaFiles.length > 0) {
        const individualProgress = {};
        const uploadPromises = mediaFiles.map((file, index) => {
          const storageRef = ref(storage, `media/${user.uid}/${Date.now()}_${file.name}`);
          const uploadTask = uploadBytesResumable(storageRef, file);

          return new Promise((resolve, reject) => {
            uploadTask.on('state_changed',
              (snapshot) => {
                individualProgress[index] = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                const total = Object.values(individualProgress).reduce((sum, progress) => sum + progress, 0);
                const overallProgress = total / mediaFiles.length;
                setUploadProgress(overallProgress);
              },
              (error) => { console.error("Upload failed:", error); reject(error); },
              async () => {
                const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                resolve({ url: downloadURL, type: file.type });
              }
            );
          });
        });

        try {
          const uploadedMedia = await Promise.all(uploadPromises);
          finalCapsuleData.media = uploadedMedia;
        } catch (error) {
          setIsUploading(false);
          return;
        }
      }

      await onSeal(finalCapsuleData);
      setIsUploading(false);
    } catch (error) {
      console.error("Error resolving recipients:", error);
      setRecipientError(error.message || "An error occurred while validating recipients.");
      setIsUploading(false);
    }
  };

  if (!isVisible) return null;

  if (recordingType) {
    return <MediaRecorderModal type={recordingType} onClose={() => setRecordingType(null)} onMediaCaptured={handleMediaCaptured} />
  }

  return (
    <div className="fixed inset-0 bg-black/80 z-50 overflow-y-auto animate-fade-in" onClick={onClose}>
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="bg-[#1E1E1E] border border-[#D4AF37]/50 rounded-xl shadow-2xl w-full max-w-lg text-[#EAEAEA] p-8 relative" onClick={e => e.stopPropagation()}>
          <h2 className="text-3xl font-bold text-[#D4AF37] mb-6 text-center">Seal a New Kiv</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <input type="text" placeholder="Title" value={title} onChange={e => setTitle(e.target.value)} className="w-full bg-gray-800 p-3 rounded-md border border-gray-700 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]" />

            <div className="relative">
              {isGroupSend ? (
                <textarea placeholder="Recipient(s) - email or @username, separated by commas" value={recipientEmail} onChange={handleRecipientChange} rows="2" className="w-full bg-gray-800 p-3 rounded-md border border-gray-700 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]" />
              ) : (
                <input type="text" placeholder="Recipient's Email or @username" value={recipientEmail} onChange={handleRecipientChange} className="w-full bg-gray-800 p-3 rounded-md border border-gray-700 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]" />
              )}
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-gray-800 border border-gray-700 rounded-md shadow-lg">
                  <ul>
                    {suggestions.map(friend => (
                      <li
                        key={friend.id}
                        onClick={() => handleSuggestionClick(friend.username)}
                        className="px-4 py-2 cursor-pointer hover:bg-gray-700"
                      >
                        @{friend.username}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {recipientError && (
              <p className="text-sm text-red-400 -mt-2">{recipientError}</p>
            )}

            <div className="flex justify-between items-center pb-2">
              <Switch.Group as="div" className="flex items-center space-x-3">
                <Switch
                  checked={isGroupSend}
                  onChange={setIsGroupSend}
                  className={`${isGroupSend ? 'bg-[#D4AF37]' : 'bg-gray-700'
                    } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:ring-offset-2 focus:ring-offset-gray-800`}
                >
                  <span
                    className={`${isGroupSend ? 'translate-x-6' : 'translate-x-1'
                      } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                  />
                </Switch>
                <Switch.Label className="cursor-pointer text-[#EAEAEA]">Send to multiple people</Switch.Label>
              </Switch.Group>
              <Switch.Group as="div" className="flex items-center space-x-3">
                <Switch
                  checked={isImmediate}
                  onChange={setIsImmediate}
                  className={`${isImmediate ? 'bg-[#D4AF37]' : 'bg-gray-700'
                    } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:ring-offset-2 focus:ring-offset-gray-800`}
                >
                  <span
                    className={`${isImmediate ? 'translate-x-6' : 'translate-x-1'
                      } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                  />
                </Switch>
                <Switch.Label className="cursor-pointer text-[#EAEAEA]">Allow Immediate Open</Switch.Label>
              </Switch.Group>
            </div>

            <Popover className="relative">
              {({ open, close }) => (
                <>
                  <Popover.Button
                    disabled={isImmediate}
                    className={`w-full bg-gray-800 p-3 rounded-md border border-gray-700 flex justify-between items-center
                    focus:outline-none focus:ring-2 focus:ring-[#D4AF37] 
                    ${isImmediate ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <span className={selectedDate ? 'text-white' : 'text-gray-400'}>
                      {selectedDate ? format(selectedDate, 'PPP HH:mm') : 'Unlock Date and Time'}
                    </span>
                    <ClockIcon className="h-5 w-5 text-gray-400" />
                  </Popover.Button>
                  <p className="text-xs text-[#A0A0A0] text-right mt-1 pr-1">Time zone: {Intl.DateTimeFormat().resolvedOptions().timeZone}</p>

                  <Popover.Panel className="absolute z-10 mt-2 bg-[#1E1E1E] border border-[#D4AF37]/50 rounded-xl shadow-2xl">
                    <div className="p-4">
                      <DayPicker
                        mode="single"
                        selected={selectedDate}
                        onSelect={handleDateSelect}
                        className="p-0"
                      />
                      <div className="flex items-center justify-center pt-4">
                        <input
                          type="time"
                          value={selectedDate ? format(selectedDate, 'HH:mm') : ''}
                          onChange={handleTimeChange}
                          className="w-full bg-gray-800 p-2 rounded-md border border-gray-700 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                        />
                      </div>
                      <div className="flex justify-end pt-4">
                        <button
                          type="button"
                          onClick={() => close()}
                          className="bg-gray-700 text-white font-bold py-2 px-4 rounded-full hover:bg-gray-600 transition-colors"
                        >
                          Done
                        </button>
                      </div>
                    </div>
                  </Popover.Panel>
                </>
              )}
            </Popover>

            <div className="relative">
              <textarea
                placeholder="Your secret message..."
                value={message}
                onChange={e => setMessage(e.target.value)}
                rows="4"
                maxLength={50000}
                className="w-full bg-gray-800 p-3 rounded-md border border-gray-700 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
              ></textarea>
              {message.length > 45000 && (
                <p className={`text-xs text-right mt-1 ${message.length >= 50000 ? 'text-red-500 font-bold' : 'text-gray-400'}`}>
                  {message.length.toLocaleString()} / 50,000 characters
                </p>
              )}
            </div>

            {/* Disappearing Options */}
            <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
              <RadioGroup value={disappearOption} onChange={setDisappearOption} as="div" className="space-y-3">
                <RadioGroup.Label className="font-bold text-gray-300">Disappear Options</RadioGroup.Label>
                <div className="flex justify-between rounded-md shadow-sm">
                  {disappearOptions.map((option, index) => (
                    <RadioGroup.Option
                      key={option.value}
                      value={option.value}
                      className={({ checked }) =>
                        `flex-1 cursor-pointer p-3 text-center text-sm font-medium
                      ${index === 0 ? 'rounded-l-md' : ''}
                      ${index === disappearOptions.length - 1 ? 'rounded-r-md' : ''}
                      ${checked
                          ? 'bg-[#D4AF37] text-black'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}
                      focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-[#D4AF37]`
                      }
                    >
                      {option.name}
                    </RadioGroup.Option>
                  ))}
                </div>
              </RadioGroup>
              {disappearOption === 'after_time' && (
                <div className="flex items-center space-x-2 pl-1 pt-2">
                  <input type="number" min="1" max="24" value={disappearHours} onChange={(e) => setDisappearHours(parseInt(e.target.value, 10))} className="bg-gray-900 p-1 rounded-md border border-gray-600 w-20 text-center" />
                  <span>hours</span>
                </div>
              )}
            </div>

            <div className="space-y-2">
              {mediaFiles.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {mediaFiles.map((file, index) => (
                    <div key={index} className="relative group">
                      {file.type.startsWith('image/') && <img src={URL.createObjectURL(file)} alt="Preview" className="w-full h-24 object-cover rounded-md" />}
                      {file.type.startsWith('video/') && <video src={URL.createObjectURL(file)} className="w-full h-24 object-cover rounded-md" />}
                      {file.type.startsWith('audio/') && <div className="w-full h-24 bg-gray-700 rounded-md flex items-center justify-center"><MicIcon className="w-8 h-8 text-white" /></div>}
                      <button type="button" onClick={() => removeMedia(index)} className="absolute top-1 right-1 bg-black/50 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100">&times;</button>
                    </div>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-2 gap-2">
                <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*,video/*,audio/*" className="hidden" multiple />
                <button type="button" onClick={() => fileInputRef.current.click()} className="bg-gray-700 p-2 rounded-md border border-dashed border-gray-600 flex items-center justify-center space-x-2 text-[#A0A0A0] hover:bg-gray-600">
                  <PaperclipIcon /> <span>Upload File(s)</span>
                </button>
                <button type="button" onClick={() => setRecordingType('photo')} className="bg-gray-700 p-2 rounded-md border border-dashed border-gray-600 flex items-center justify-center space-x-2 text-[#A0A0A0] hover:bg-gray-600">
                  <CameraIcon /> <span>Take Photo</span>
                </button>
                <button type="button" onClick={() => setRecordingType('video')} className="bg-gray-700 p-2 rounded-md border border-dashed border-gray-600 flex items-center justify-center space-x-2 text-[#A0A0A0] hover:bg-gray-600">
                  <VideoIcon /> <span>Record Video</span>
                </button>
                <button type="button" onClick={() => setRecordingType('audio')} className="bg-gray-700 p-2 rounded-md border border-dashed border-gray-600 flex items-center justify-center space-x-2 text-[#A0A0A0] hover:bg-gray-600">
                  <MicIcon /> <span>Record Audio</span>
                </button>
              </div>
              <p className="text-xs text-text-secondary text-center">Future Feature: Limit file size, type, and count for monetization.</p>
            </div>

            {isUploading && (
              <div className="w-full bg-gray-700 rounded-full h-2.5">
                <div className="bg-[#4E9A91] h-2.5 rounded-full" style={{ width: `${uploadProgress}%` }}></div>
              </div>
            )}

            <div className="flex justify-end space-x-4 pt-4">
              <button type="button" onClick={onClose} className="bg-gray-700 text-white font-bold py-2 px-6 rounded-full hover:bg-gray-600 transition-colors" disabled={isUploading}>Cancel</button>
              <button type="submit" className="bg-[#D4AF37] text-[#121212] font-bold py-2 px-6 rounded-full hover:bg-[#C09A2E] transition-colors" disabled={isUploading}>
                {isUploading ? `Uploading... ${Math.round(uploadProgress)}%` : 'Seal Kiv'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateCapsuleModal;